import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { Config } from 'src/app/config';
import { Observable, of, throwError } from 'rxjs';
import { BoundingBoxAnchorResult, CatMatch, ConsistencyIssuesToUpdateDto, DataElement, FileValidationParams, FileValidationResponse, ReferencePhrase, ServiceCallInfo } from '@shared/models/common';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { actionsList, AppState } from '@shared/redux/store';
import { NgRedux } from '@shared/redux/redux-compatibility.service';
import { CatMatchPhraseMetadataModel } from 'src/app/cat-doc/cat-match-phrase-metadata/model/cat-match-phrase-metadata.model';
import { ParentTileChildrenSelectionResponse } from '@shared/models/parent-tile-children-selection-response.model';
import { Report, ReportDefinition } from '@shared/models/user.model';
import { RevRule } from '@shared/models/rev.model';
import { IndPhrase } from '@shared/models/cat.model';
import { SharedService } from './shared.service';
import { InfoMessageInitializer } from '@shared/models/infomessage-initializer';
@Injectable({
  providedIn: 'root'
})
export class CommonService {
  
  scaiServiceUrl: string;

  constructor(
    private ngRedux: NgRedux<AppState>,
    private http: HttpClient,
    private authService: AuthService,
    private sharedService: SharedService
  ) { 
    this.scaiServiceUrl = Config.assetConfigData['scaiServiceUrl'];
  }

  //add call to get claim metadata
  getCatMatchPhraseMetadata(catVersionId: number, phraseId: number): Observable<CatMatchPhraseMetadataModel[]> {
    const url = this.scaiServiceUrl + `claims/${catVersionId}/${phraseId}/metadata`;
    return this.http
      .get<CatMatchPhraseMetadataModel[]>(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      });
  }
  getContentTypeList(): Observable<DataElement[]> {
    const url = this.scaiServiceUrl + `content-types`;

    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response: Array<any>) => {
          let results = response.map(el => new DataElement(el.content_type_code, el.content_type_desc));
          
          this.ngRedux.dispatch({
            type: actionsList.GET_CONTENT_TYPE_LIST,
            data: results,
          });

          return results;
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  getRuleSimilarityEventList(): Observable<void> {
    const url = this.scaiServiceUrl + `rule-similarity-events`;

    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response: Array<any>) => {
          let revResults = response.map(el => new DataElement(el.rule_similarity_event_code, el.rule_similarity_event_desc));
          let indResults = response.map(el => new DataElement(el.rule_similarity_event_code, el.ind_similarity_event_desc));
          
          this.ngRedux.dispatch({
            type: actionsList.GET_REV_RULE_SIMILARITY_EVENT_LIST,
            data: revResults,
          });

          this.ngRedux.dispatch({
            type: actionsList.GET_IND_RULE_SIMILARITY_EVENT_LIST,
            data: indResults,
          });
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }
  
  getReportsList(envId: number): Observable<Report[]> {
    const url = this.scaiServiceUrl + `reports`;

    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
        params: {env_id: envId}
      })
      .pipe(
        map((response: any[]) => {
          let results = this._extractReports(response);

          results.sort((a, b) => {
            
            return new Date(b.executedOn).getTime() - new Date(a.executedOn).getTime();
          });
          this.ngRedux.dispatch({
            type: actionsList.GET_REPORTS_LIST,
            data: results
          });

          return results;
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  private _extractReports(res: any[]): Report[] {
    let reports: Report[] = [];
    res.forEach(item => {
      
      let report = this.extractReport(item);
      reports.push(report);
    });
    return reports;
  }

  private extractReport(item: any) : Report {
      let report: Report = new Report();
      report.id = item.id;
      report.reportName = item.report_name;
      report.reportLocation = item.report_location;
      report.reportDefId = item.report_def_id;
      report.executedOn = item.executed_on;
      report.executedBy = {
        email: item.executed_by.email,
        id: item.executed_by.id,
        username: item.executed_by.username,
        usrDescr: item.executed_by.usr_descr
      }
      report.endDate = item.end_date;
      report.startDate = item.start_date;
      report.envId = item.env_id;
      return report;
  }

  private handleError(error: any, url: string): Observable<never> {
    const info = new ServiceCallInfo(url, url, null);
    error.message = error.error.description || error.statusText;

    return this.authService.handleError(error, info);
  }

  addAlgRunHead(algRunHeadData: object): Observable<number> {
    let url = `${this.scaiServiceUrl}alg-run-heads`;
    return this.http
      .post(url, algRunHeadData, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
      .pipe(
        take(1),
        map((data: any) => {
          if (data) {
            return data.run_num;
          }
        }),
        catchError((err) => {
          return this.handleError(err, url);
        })
      );
  }

  getGenaiConfigTemplateList(): Observable<void> {
    const url = this.scaiServiceUrl + `genai-config-templates`;

    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response: Array<any>) => {
          let results = response.map(el => new DataElement(el.genai_config_template_code, el.genai_config_template_desc));
          
          this.ngRedux.dispatch({
            type: actionsList.GET_GENAI_CONFIG_TEMPLATE_LIST,
            data: results,
          });
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  getEmbeddingConfigTemplateList(): Observable<void> {
    const url = this.scaiServiceUrl + `embedding-config-templates`;

    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response: Array<any>) => {
          let results = response.map(el => new DataElement(el.embedding_config_template_code, el.embedding_config_template_desc));
          
          this.ngRedux.dispatch({
            type: actionsList.GET_EMBEDDING_CONFIG_TEMPLATE_LIST,
            data: results,
          });
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  getGrammarSpellingCheckHandlersList(): Observable<void> {
    const url = this.scaiServiceUrl + `grammar-spelling/check-handlers`;

    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response: Array<any>) => {
          let results = response.map(el => new DataElement(el.grammar_spelling_check_handler_code, el.grammar_spelling_check_handler_desc));
          
          this.ngRedux.dispatch({
            type: actionsList.GET_GRAMMAR_SPELLING_CHECK_HANDLER_LIST,
            data: results,
          });
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  getClaimsManagementTypesList(): Observable<void> {
    const url = this.scaiServiceUrl + `claims-management-types`;

    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response: Array<any>) => {
          let results = response.map(el => new DataElement(el.claims_management_type, el.claims_management_type_descr));
          
          this.ngRedux.dispatch({
            type: actionsList.GET_CLAIMS_MANAGEMENT_TYPES,
            data: results,
          });
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  getComplianceMaterialsTemplateList(): Observable<void> {
    const url = this.scaiServiceUrl + `compliance-materials-templates`;

    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response: Array<any>) => {
          let results = response.map(el => new DataElement(el.compliance_materials_template_code, el.compliance_materials_template_desc));
          
          this.ngRedux.dispatch({
            type: actionsList.GET_COMPLIANCE_MATERIALS_TEMPLATE_LIST,
            data: results,
          });
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  provisionSsoUser(): Observable<any> {
    const url = this.scaiServiceUrl + 'users/sso/provision';
    return this.http
      .post(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      });
  }
  public getChildrenSelection(docId:number, phraseId: number, action: string, isPrecheck: boolean): Observable<ParentTileChildrenSelectionResponse> {

      let url = this.scaiServiceUrl + `claims/propagate-action-candidates?doc_id=${docId}&phrase_id=${phraseId}&is_precheck=${isPrecheck}`;
      if (action != null) {
        url = url + `&action=${action}`;
      }
      return this.http
        .get<ParentTileChildrenSelectionResponse>(url, {
          headers: this.authService.getRequestHeaders(),
          withCredentials: false,
          responseType: 'json'
        });
  }
  public updateConsistencyIssues(anchorsToUpdate: ConsistencyIssuesToUpdateDto) {
      let url = this.scaiServiceUrl + `genai_check_results`;

      return this.http.post(url, anchorsToUpdate,
        {
          headers: this.authService.getRequestHeaders(),
         withCredentials: false, 
         responseType: 'json'
    });
  }

  /** Submits consistency-issue changes and emits the run number of the re-evaluation they start. */
  startConsistencyIssuesRun(consistencyIssuesToUpdate: ConsistencyIssuesToUpdateDto): Observable<number> {
    return this.updateConsistencyIssues(consistencyIssuesToUpdate).pipe(
      switchMap((response: any) => {
        const runNum = response?.run_num;
        if (!runNum) {
          console.error('No run_num returned from updateConsistencyIssues');
          return throwError(new Error('Missing run_num in response'));
        }
        return of(runNum as number);
      })
    );
  }
  getImageCategoriesForDocument(docId: number, isPrecheck: boolean): Observable<void> {
    const url = this.scaiServiceUrl + `image-categories?doc_id=${docId}&is_precheck=${isPrecheck}`;
    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      }).pipe(map((data) => {
          
          this.ngRedux.dispatch({
            type: actionsList.GET_IMAGE_CATEGORIES_FOR_DOC,
            data: data['image_categories'] as string[]
          });
        }),
        catchError((err) => {
           this.handleError(err, url);
           return of(null);
        }))

  }

  updateImageCategory(docId: number, imageId: number, category: string, isPrecheck: boolean = false) {
    const payload = {
      doc_id: docId,
      image_id: imageId,
      category: category,
      is_precheck: isPrecheck
    };

    return this.http.put(this.scaiServiceUrl + 'image-category', payload);
  }

  getUiConfig(envId: number, resolveConfig: boolean = true): Observable<object> {
    let url = this.scaiServiceUrl + 'ui-config';
    if (envId) {
      url = url.concat(`?env_id=${envId}&resolveConfig=${resolveConfig}`);
    }

    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response: object) => {
          return response;
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  setUiConfig(envId: number, ui_config: object): Observable<object> {
    const url = this.scaiServiceUrl + `ui-config?env_id=${envId}`;

    return this.http
      .post(url, { ...ui_config }, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response: object) => {
          return response;
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }
  validateFile(validationParams: FileValidationParams) {

    const url = this.scaiServiceUrl + `validate-document`;
    return this.http
      .post(url, validationParams, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      }).pipe(
        map((response) => {
          let fvr = new FileValidationResponse();
          fvr.is_valid =  response['is_valid'] === 'True' || response['is_valid'] === true
          fvr.error_message = response['error_message']
          return fvr;
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      )
    
  }
  getReportDefinitions(envId: number): Observable<ReportDefinition[]> {
    
      return this.http.get<ReportDefinition[]>(this.scaiServiceUrl + 'report-definitions', {
          headers: this.authService.getRequestHeaders(),
          withCredentials: false,
          responseType: 'json',
          params: {env_id: envId}
      }).pipe(
          map((response: any[]) => {
              return this._extractReportDefinitions(response);
          }),
          catchError((err) => {
              return this.handleError(err, this.scaiServiceUrl + 'report-definitions')
              
          })
      );
  }
  addReport(report: Report): Observable<Report> {
    const url = this.scaiServiceUrl + `reports`;
    const payload = {
      report_name: report.reportName, 
      report_def_id: report.reportDefId,
      start_date: report.startDate,
      end_date: report.endDate,
      env_id: report.envId
    };
    return this.http.post<Report>(url, payload, {
      headers: this.authService.getRequestHeaders(),
      withCredentials: false,
      responseType: 'json'
    }).pipe(
      map((response: any) => {
        return this.extractReport(response);
      }),
      catchError((err) => {
        return this.handleError(err, url)
      })
    );
  }
  createBoundingBoxesAnchors(boundingBoxesAnchors: BoundingBoxAnchorResult[], catVersionId: number, phrase: IndPhrase | RevRule | null) {
    const newAnchors = boundingBoxesAnchors.filter(b => b.refDocAnchorId == null);
    const existingAnchors = boundingBoxesAnchors.filter(b => b.refDocAnchorId != null);
    existingAnchors.forEach((box) => {
      phrase?.consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results.push({
        issue_id: null,
        ref_doc_id: box.refDocId,
        ref_doc_anchor_id: box.refDocAnchorId
      });
    });

    if (newAnchors.length === 0) {
      return of([]);
    }

    const payload = [];
    newAnchors.forEach((box) => {
      payload.push({
        ref_doc_id: box.refDocId,
        status: "CONSISTENT",
        anchor_path: box.embeddedObjectAnnotation.anchorPath,
        anchor_phrases_ids: box.phraseIds,
        anchor_images: box.imagesInfos
      })
    })

    const url = `${this.scaiServiceUrl}catalogs/${catVersionId}/reference-docs/anchors`;

    return this.http.post<any[]>(url, payload, {
      headers: this.authService.getRequestHeaders(),
      withCredentials: false,
      responseType: 'json'
    }).pipe(
      map((response: any[]) => {

        for (let i=0; i<response.length; i++) {
          newAnchors[i].refDocAnchorId = response[i].ref_doc_anchor_id;
          newAnchors[i].refDocId = response[i].ref_doc_id
          if (phrase) {
            phrase.consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results.push({
              issue_id: null,
              ref_doc_id: newAnchors[i].refDocId,
              ref_doc_anchor_id: newAnchors[i].refDocAnchorId
            });
          }
        }
        return newAnchors;


      }),
      catchError((err) => {
            const infoMessage: InfoMessageInitializer = {
              infoType: 'error',
              titleKey: 'translate.anchors.create-bounding-box-anchors-title',
              messageKey: 'translate.anchors.create-bounding-box-anchors-message'
            };
            this.sharedService.showInfoMessage(infoMessage);
        throw err;
      })
    );

  }

  hasIssuesToUpdate(consistencyIssuesToUpdate: ConsistencyIssuesToUpdateDto): boolean {
    const hasRemoveIssues = Object.keys(consistencyIssuesToUpdate.remove).filter(key => consistencyIssuesToUpdate.remove[key].length > 0).length > 0
    const hasAnchors = consistencyIssuesToUpdate.convert.anchors.length > 0;
    const hasAnchorsToAdd = consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results.length > 0;

    return hasRemoveIssues || hasAnchors || hasAnchorsToAdd;
  }

  updateAnchors(catVersionId: number, phrase: IndPhrase | RevRule) {
  
    const url = this.scaiServiceUrl + `/catalogs/${catVersionId}/reference-docs/anchors`;
    const payload = phrase.anchorsPathsToUpdate;

    
    return this.http.patch(url, payload, {
      headers: this.authService.getRequestHeaders(),
      withCredentials: false,
      responseType: 'json'
    }).pipe(
      map((response: any) => {

        return of({
          type: "success",
          data: response

        })
        
      }),
      catchError((err) => {
         this.handleError(err, url)
         return of({
          type: "error",
          data: err
         })
      })
    );
  }
  

  private _extractReportDefinitions(res: any[]): ReportDefinition[] { 


    let reportDefinitions: ReportDefinition[] = [];
    res.forEach(item => {

      let reportDefinition: ReportDefinition = new ReportDefinition();
      reportDefinition.id = item.id;
      reportDefinition.name = item.report_name;
      reportDefinitions.push(reportDefinition);
    });
    return reportDefinitions;
  }

  public setRefConsistencyParamIfAllAnchorsRemoved(phrase: IndPhrase | RevRule) {
    if (this.checkIfAllAnchorsAreRemoved(phrase)) {
      
      phrase.consistencyIssuesToUpdate.is_ref_consistent_anchor_update_only = false;
    }
  }
  private getMatches(phrase: IndPhrase | RevRule): CatMatch[] {

    if (phrase instanceof IndPhrase) {
      return phrase.catMatches;
    }
    return phrase.ruleCatMatches;

  }
  private checkIfAllAnchorsAreRemoved(phrase: IndPhrase | RevRule): boolean {
    let allDeleted = false;

      for (let ref of phrase.references) {

        if (!ref.isDeletedAnchor || !ref.isConsistencyGenerated) {
          
          continue;
        }
        
        // SKIP ADDED_REFERENCE
        let addedAnchorReference = phrase.referenceIssues?.find(refIssue => ref.refDocId == refIssue.refDocId && refIssue.refFinding === 'MISSING_ANCHOR_REFERENCE');
        let hasReferenceOnRight = false;
        
        if (addedAnchorReference) {
          
          hasReferenceOnRight = true;
        } 
        // check for match
        if (!hasReferenceOnRight) {
          const catMatches: CatMatch[] = this.getMatches(phrase);
          let catMatchIndex = catMatches?.findIndex((match) => {
                  
              let references: ReferencePhrase[] = match.catPhrase.references?.filter(ref => ref.isConsistencyGenerated);
              let foundMatchingReference = references.find(catMatchRef => catMatchRef.catPhraseId === match.catPhrase.phraseId && ref.refDocId === catMatchRef.refDocId);
              return foundMatchingReference != undefined;
          });
          
          if (catMatchIndex != -1) {
            
            hasReferenceOnRight = true;
          }
        }
        if (hasReferenceOnRight && ref.anchors && ref.anchors.length == 0) {
          allDeleted = true;
          break;
        }
      }


    
    return allDeleted;
  }
}